import { fmtQty, longDate, money, taxBreakdown } from '../../utils/invoices';

const Lines = ({ text, className = '' }) =>
  (text || '').split(/\r?\n/).filter(Boolean).map((l, i) => <div key={i} className={className}>{l}</div>);

/**
 * An invoice on screen (same content as the PDF): editor preview, admin view, public /i/:token.
 * doc = invoiceDocument() from utils/invoices.js.
 */
export default function InvoiceDocument({ doc, className = '' }) {
  const s = doc.seller || {};
  const c = doc.client || {};
  const due = doc.total_cents - (doc.paid_cents || 0);
  return (
    <article className={`bg-white text-ink rounded-2xl border border-line shadow-[0_1px_2px_rgba(11,21,48,0.06)] p-6 sm:p-10 text-[14px] leading-[20px] ${className}`}
      aria-label={`Invoice ${doc.number || 'draft'}`}>
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          {s.logo_url
            ? <img src={s.logo_url} alt={s.name} className="max-h-14 max-w-[200px] object-contain object-left" />
            : <div className="font-display text-[22px] font-extrabold">{s.name}</div>}
        </div>
        <div className="text-right">
          <div className="font-display text-[28px] leading-8 font-extrabold text-blue tracking-tight">INVOICE</div>
          <div className="font-semibold">{doc.number ? `# ${doc.number}` : 'Draft'}</div>
        </div>
      </header>

      <section className="grid sm:grid-cols-3 gap-6 mt-8">
        <div>
          <div className="text-[11px] font-bold tracking-wider text-ink-muted mb-1">FROM</div>
          <div className="font-semibold">{s.name}</div>
          <Lines text={s.address} />
          {[s.phone, s.email, s.website].filter(Boolean).map((v) => <div key={v}>{v}</div>)}
          {s.ein && <div>EIN {s.ein}</div>}
        </div>
        <div>
          <div className="text-[11px] font-bold tracking-wider text-ink-muted mb-1">BILL TO</div>
          <div className="font-semibold">{c.name || '—'}</div>
          {c.contact_name && <div>Attn: {c.contact_name}</div>}
          <Lines text={c.address} />
          {c.tax_exempt && <div className="text-ink-muted">Tax exempt{c.tax_exempt_id ? ` #${c.tax_exempt_id}` : ''}</div>}
        </div>
        <div className="sm:text-right space-y-1">
          <div><span className="text-ink-muted">Issue date</span> <strong>{longDate(doc.issue_date)}</strong></div>
          <div><span className="text-ink-muted">Due date</span> <strong>{longDate(doc.due_date)}</strong></div>
          {doc.po_number && <div><span className="text-ink-muted">PO</span> <strong>{doc.po_number}</strong></div>}
          <div className="inline-block mt-2 rounded-xl bg-surface px-4 py-2 text-left">
            <div className="text-[12px] text-ink-muted">Amount due</div>
            <div className="text-[20px] font-extrabold tabular-nums">{money(due)}</div>
          </div>
        </div>
      </section>

      <div className="mt-8 overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="bg-surface text-[11px] font-bold tracking-wider text-ink-muted text-left">
              <th className="px-3 py-2">DESCRIPTION</th>
              <th className="px-3 py-2 text-right">QTY</th>
              <th className="px-3 py-2 text-right">PRICE</th>
              <th className="px-3 py-2 text-right">AMOUNT</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {doc.lines.length === 0 && <tr><td colSpan="4" className="px-3 py-4 text-ink-muted">No lines yet.</td></tr>}
            {doc.lines.map((l, i) => (
              <tr key={i} className="align-top">
                <td className="px-3 py-2.5 whitespace-pre-line">{l.description}</td>
                <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                  {fmtQty(l.quantity)}{l.unit && l.unit !== 'each' ? ` ${l.unit}${Number(l.quantity) === 1 ? '' : 's'}` : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{money(l.unit_price_cents)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {money(l.amount_cents)}
                  {l.taxable && (l.taxes || []).length > 0 && <span className="ml-1 text-[10px] text-ink-muted align-top" title="Taxed">T</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className="mt-4 flex justify-end">
        <dl className="w-full max-w-xs grid grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 tabular-nums">
          <dt className="text-ink-muted text-right">Subtotal</dt><dd className="text-right">{money(doc.subtotal_cents)}</dd>
          {doc.discount_cents > 0 && (<><dt className="text-ink-muted text-right">Discount</dt><dd className="text-right">-{money(doc.discount_cents)}</dd></>)}
          {taxBreakdown(doc.lines).map((t) => (
            <FragmentRow key={`${t.name}${t.rate}`} label={`${t.name} (${t.rate}%)`} value={money(t.cents)} />
          ))}
          <dt className="text-right font-bold pt-1 border-t border-line">Total</dt><dd className="text-right font-bold pt-1 border-t border-line">{money(doc.total_cents)}</dd>
          {doc.paid_cents > 0 && (<><dt className="text-ink-muted text-right">Paid</dt><dd className="text-right">-{money(doc.paid_cents)}</dd></>)}
          <dt className="text-right font-extrabold text-blue">Amount due</dt><dd className="text-right font-extrabold text-blue">{money(due)}</dd>
        </dl>
      </section>

      <section className="mt-8 grid sm:grid-cols-2 gap-6">
        <div>
          <div className="text-[11px] font-bold tracking-wider text-ink-muted mb-1">HOW TO PAY</div>
          <div>By check, payable to <strong>{s.check_payable_to || s.name}</strong></div>
          {s.remit_address && <div className="mt-1"><span className="text-ink-muted">Mail to</span><Lines text={s.remit_address} /></div>}
          {doc.number && <div className="mt-1 text-ink-muted">Please write invoice {doc.number} on the memo line.</div>}
        </div>
        <div className="space-y-4">
          {doc.notes && <div><div className="text-[11px] font-bold tracking-wider text-ink-muted mb-1">NOTES</div><div className="whitespace-pre-line">{doc.notes}</div></div>}
          {doc.terms && <div><div className="text-[11px] font-bold tracking-wider text-ink-muted mb-1">TERMS</div><div className="whitespace-pre-line">{doc.terms}</div></div>}
        </div>
      </section>
      {s.footer && <footer className="mt-8 pt-4 border-t border-line text-center text-[12px] text-ink-muted whitespace-pre-line">{s.footer}</footer>}
    </article>
  );
}

function FragmentRow({ label, value }) {
  return (<><dt className="text-ink-muted text-right">{label}</dt><dd className="text-right">{value}</dd></>);
}
